import React, { useState, useEffect } from 'react';
import api from '../api/client';
import '@fortawesome/fontawesome-free/css/all.min.css';

const CarriersPopup = ({ isOpen, onClose }) => {
    const [carriers, setCarriers] = useState([]);
    const [name, setName] = useState('');
    const [requireProof, setRequireProof] = useState(false);
    const [editingID, setEditingID] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) fetchCarriers();
    }, [isOpen]);

    const fetchCarriers = async () => {
        try {
            const res = await api.get('/api/amz-carriers');
            setCarriers(res.data);
        } catch {
            setError('Failed to load carriers');
        }
    };

    const resetForm = () => {
        setName('');
        setRequireProof(false);
        setEditingID(null);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { setError('Carrier name is required'); return; }
        setLoading(true);
        try {
            if (editingID) {
                await api.put(`/api/amz-carriers/${editingID}`, { name, require_proof: requireProof });
            } else {
                await api.post('/api/amz-carriers', { name, require_proof: requireProof });
            }
            resetForm();
            fetchCarriers();
        } catch {
            setError('Failed to save carrier');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (carrier) => {
        setEditingID(carrier.carrierID);
        setName(carrier.name);
        setRequireProof(!!carrier.require_proof);
        setError('');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this carrier?')) return;
        try {
            await api.delete(`/api/amz-carriers/${id}`);
            if (editingID === id) resetForm();
            fetchCarriers();
        } catch {
            setError('Failed to delete carrier');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="amz-popup-overlay">
            <div className="amz-popup-content" style={{ maxWidth: 520, width: '100%' }}>
                <h2 className="amz-popup-title">Carriers</h2>
                <p className="amz-popup-subtitle">Manage shipping carriers and proof of delivery settings</p>
                <button className="amz-close-btn" onClick={onClose}>
                    <i className="fas fa-times" />
                </button>

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ marginBottom: 8 }}>
                    <h3 className="amz-form-subtitle">{editingID ? 'Edit Carrier' : 'Add Carrier'}</h3>

                    <div className="amz-form-group">
                        <label>Carrier Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. UPS, FedEx, USPS"
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                        />
                    </div>

                    <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                            <div
                                onClick={() => setRequireProof(p => !p)}
                                style={{
                                    width: 40, height: 22, borderRadius: 11,
                                    background: requireProof ? '#5B965D' : '#d1d5db',
                                    position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: 3, left: requireProof ? 21 : 3,
                                    width: 16, height: 16, borderRadius: '50%',
                                    background: '#fff', transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                            </div>
                            <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Require Proof of Delivery</span>
                        </label>
                    </div>

                    {error && (
                        <p className="amz-error">{error}</p>
                    )}

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            type="submit"
                            disabled={loading}
                            className="amz-submit-btn"
                            style={{ minWidth: 130 }}
                        >
                            {loading
                                ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Saving...</>
                                : editingID
                                    ? <><i className="fas fa-check" style={{ marginRight: 6 }} />Save Changes</>
                                    : <><i className="fas fa-plus" style={{ marginRight: 6 }} />Add Carrier</>
                            }
                        </button>
                        {editingID && (
                            <button type="button" className="amz-cancel-btn" onClick={resetForm}>
                                Cancel
                            </button>
                        )}
                    </div>
                </form>

                {/* Carriers list */}
                <h3 className="amz-form-subtitle" style={{ marginTop: 28 }}>Existing Carriers</h3>

                {carriers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                        <i className="fas fa-truck" style={{ fontSize: 28, marginBottom: 10, display: 'block', color: '#d1d5db' }} />
                        <p style={{ margin: 0, fontSize: 14 }}>No carriers added yet</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                        {carriers.map(c => (
                            <div
                                key={c.carrierID}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '12px 16px', borderRadius: 10,
                                    border: editingID === c.carrierID ? '1.5px solid #5B965D' : '1.5px solid #f0f0f0',
                                    background: editingID === c.carrierID ? '#f0fdf4' : '#fafafa',
                                    transition: 'border-color 0.15s, background 0.15s'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <i className="fas fa-truck" style={{ color: '#9ca3af', fontSize: 14 }} />
                                    <span style={{ fontWeight: 600, fontSize: 14, color: '#111' }}>{c.name}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                        background: c.require_proof ? '#fef3c7' : '#f0fdf4',
                                        color: c.require_proof ? '#92400e' : '#166534'
                                    }}>
                                        {c.require_proof
                                            ? <><i className="fas fa-file-circle-check" style={{ fontSize: 11 }} /> Proof required</>
                                            : <><i className="fas fa-check" style={{ fontSize: 11 }} /> No proof</>
                                        }
                                    </span>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button
                                            type="button"
                                            onClick={() => handleEdit(c)}
                                            title="Edit"
                                            style={{
                                                width: 32, height: 32, borderRadius: 6,
                                                border: '1px solid #e5e7eb', background: '#fff',
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#6b7280', transition: 'color 0.15s, background 0.15s, border-color 0.15s'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#5B965D'; e.currentTarget.style.color = '#5B965D'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
                                        >
                                            <i className="fas fa-pen" style={{ fontSize: 12 }} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(c.carrierID)}
                                            title="Delete"
                                            style={{
                                                width: 32, height: 32, borderRadius: 6,
                                                border: '1px solid #e5e7eb', background: '#fff',
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#6b7280', transition: 'color 0.15s, background 0.15s, border-color 0.15s'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#dc2626'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
                                        >
                                            <i className="fas fa-trash" style={{ fontSize: 12, margin: 0 }} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="amz-form-actions" style={{ marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                    <button type="button" className="amz-cancel-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default CarriersPopup;
